/**
 * Price-range validation helpers shared by the PLP filter drawer (client) and
 * the listing loaders (server).
 *
 * A `pmin`/`pmax` pair where `pmin > pmax` describes an impossible band — CIO
 * evaluates the browse/search range filter as "price ≥ pmin AND price ≤ pmax",
 * so a backwards range silently zeroes out the result set. These helpers detect
 * that case so the drawer can show an inline error on manual entry and the
 * loader can clamp a backwards URL (`?pmin=100&pmax=10`) before it reaches CIO.
 *
 * The drawer also uses the `-inf` / `inf` sentinels for an open-ended bound;
 * `parsePriceBound` returns `null` for those (and any non-numeric value), so an
 * open-ended range is never treated as invalid.
 */

/**
 * Parse a price-bound value (URL param or drawer input) to a finite number.
 * Returns `null` for a missing, blank, sentinel (`-inf` / `inf`), or otherwise
 * non-numeric bound — i.e. an "unbounded" end that can't make the range invalid.
 */
export function parsePriceBound(
  value: string | null | undefined,
): number | null {
  if (value == null) return null;
  const trimmed = String(value).trim();
  if (trimmed === "") return null;
  const n = Number(trimmed);
  return Number.isFinite(n) ? n : null;
}

/**
 * True only when BOTH bounds are real finite numbers and the minimum exceeds
 * the maximum. Either bound missing / open-ended is a valid (one-sided) range.
 */
export function isInvalidPriceRange(
  min: string | null | undefined,
  max: string | null | undefined,
): boolean {
  const lo = parsePriceBound(min);
  const hi = parsePriceBound(max);
  return lo !== null && hi !== null && lo > hi;
}

/** An active price filter as numbers; either side open. */
export interface PriceBounds {
  min?: number;
  max?: number;
}

/**
 * pmin/pmax URL params → numeric bounds. Backwards range clamps min down to
 * max (an inverted band zeroes out CIO results). No usable bound = undefined.
 */
export function effectivePriceBounds(
  pmin: string | null | undefined,
  pmax: string | null | undefined,
): PriceBounds | undefined {
  const min = parsePriceBound(pmin) ?? undefined;
  const max = parsePriceBound(pmax) ?? undefined;
  if (min == null && max == null) return undefined;
  if (min != null && max != null && min > max) return { min: max, max };
  return { min, max };
}

/**
 * Bounds → CIO range wire form ("0-50", "-inf-25", "10-inf"). `-inf`/`inf` =
 * unbounded end; the bare `*` wildcard returns zero results (SFCC-11151).
 * `fromCioPriceRange` below is the inverse.
 */
export function toCioPriceRange(bounds: PriceBounds): string {
  return `${bounds.min ?? "-inf"}-${bounds.max ?? "inf"}`;
}

/**
 * CIO wire form → numeric bounds. Split on LAST dash ("-inf" may lead), each
 * side through parsePriceBound. Malformed or fully open = undefined.
 */
export function fromCioPriceRange(
  rangeStr: string | undefined,
): PriceBounds | undefined {
  const cut = rangeStr?.lastIndexOf("-") ?? -1;
  if (rangeStr == null || cut <= 0) return undefined;
  const min = parsePriceBound(rangeStr.slice(0, cut)) ?? undefined;
  const max = parsePriceBound(rangeStr.slice(cut + 1)) ?? undefined;
  if (min == null && max == null) return undefined;
  return { min, max };
}
